/**
 * Tiny pub/sub to coordinate runtime + plugins without tight coupling.
 */
export type EventHandler<T> = (payload: T) => void;

export function createEventBus<T extends Record<string, unknown>>() {
  const handlers = new Map<keyof T, Set<EventHandler<any>>>();

  const on = <K extends keyof T>(event: K, handler: EventHandler<T[K]>) => {
    if (!handlers.has(event)) {
      handlers.set(event, new Set());
    }
    handlers.get(event)!.add(handler as EventHandler<any>);
    return () => {
      handlers.get(event)?.delete(handler as EventHandler<any>);
    };
  };

  const emit = <K extends keyof T>(event: K, payload: T[K]) => {
    handlers.get(event)?.forEach((handler) => {
      try {
        handler(payload);
      } catch {
        // ignore
      }
    });
  };

  return { on, emit };
}
